import { CustomTooltip } from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';
import type { User } from '@/graphql/gql-types';
import type { ColumnDef } from '@tanstack/react-table';
import { format, formatDistanceToNow } from 'date-fns';
import { labels } from 'locales/en.label';
import { ArrowUpDown, Logs, Pencil, ShieldCheck } from 'lucide-react';
import { RoleOptions } from '.';
import { EditUserPopup } from './edit-user-popup';
import { ViewUserLogs } from './view-user-logs';

export const columns: ColumnDef<User>[] = [
	{
		accessorKey: 'name',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.name}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => (
			<div className='tw-font-medium'>{row.getValue('name')}</div>
		),
	},
	{
		accessorKey: 'email',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.email}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => <div>{row.getValue('email')}</div>,
	},
	{
		accessorKey: 'super_admin',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.super_admin}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => {
			const isSuperAdmin = row.getValue('super_admin') as boolean;

			return (
				<div className='tw-flex tw-justify-center'>
					{isSuperAdmin ? (
						<CustomTooltip
							content={<p>{labels.admin.user_table.header.super_admin}</p>}
						>
							<div className='tw-text-[#55c652]'>
								<ShieldCheck className='tw-h-5 tw-w-5' />
							</div>
						</CustomTooltip>
					) : (
						<span className='tw-text-muted-foreground'>—</span>
					)}
				</div>
			);
		},
		filterFn: (row, id, value) => {
			return value.includes(String(row.getValue(id)));
		},
	},
	{
		accessorKey: 'role',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.role}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => (
			<div>
				{
					RoleOptions.find((option) => option.value === row.getValue('role'))
						?.label
				}
			</div>
		),
		filterFn: (row, id, value) => {
			return value === 'all' ? true : row.getValue(id) === value;
		},
	},
	{
		accessorKey: 'prop_id',
		header: 'Assigned Properties',
		cell: ({ row }) => {
			const properties = (row.getValue('prop_id') as string[]) || [];
			return (
				<CustomTooltip
					content={
						<div className='tw-max-w-xs'>
							<p className='tw-font-semibold'>Assigned Properties:</p>
							<ul className='tw-list-disc tw-pl-4 tw-mt-1'>
								{properties.map((prop, index) => (
									<li key={index}>{prop}</li>
								))}
							</ul>
						</div>
					}
				>
					<div>
						{properties.length > 0
							? `${properties.slice(0, 1).join(', ')}${properties.length > 1 ? ' ...' : ''}`
							: 'No properties'}
					</div>
				</CustomTooltip>
			);
		},
	},
	{
		accessorKey: 'okta_id',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.okta_id}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => <div>{row.getValue('okta_id')}</div>,
	},
	{
		accessorKey: 'login_id',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.login_id}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => <div>{row.getValue('login_id')}</div>,
	},
	{
		accessorKey: 'last_login',
		header: ({ column }) => {
			return (
				<Button
					variant='ghost'
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					{labels.admin.user_table.header.last_login}
					<ArrowUpDown className='tw-ml-2 tw-h-4 tw-w-4' />
				</Button>
			);
		},
		cell: ({ row }) => {
			const lastLogin = row.getValue('last_login') as string | null;
			if (!lastLogin)
				return <div className='tw-text-muted-foreground'>Never</div>;
			const date = new Date(lastLogin);
			return (
				<CustomTooltip content={<p>{format(date, 'yyyy-MM-dd HH:mm')}</p>}>
					<div>{formatDistanceToNow(date, { addSuffix: true })}</div>
				</CustomTooltip>
			);
		},
		sortingFn: (rowA, rowB, columnId) => {
			const a = rowA.getValue(columnId) as string | null;
			const b = rowB.getValue(columnId) as string | null;
			// Handle null values (never logged in)
			if (a === null && b === null) return 0;
			if (a === null) return 1;
			if (b === null) return -1;
			return new Date(a).getTime() - new Date(b).getTime();
		},
	},

	{
		id: 'edit_user_action',
		cell: ({ row }) => {
			const user = row.original;
			return (
				<div className='tw-flex tw-justify-end'>
					<EditUserPopup user={user}>
						<Button variant='ghost' size='icon' className='tw-h-8 tw-w-8'>
							<Pencil className='tw-h-4 tw-w-4' />
						</Button>
					</EditUserPopup>
				</div>
			);
		},
	},
	{
		id: 'view_log_action',
		cell: ({ row }) => {
			const user = row.original;
			return (
				<div className='tw-flex tw-justify-end'>
					{user.okta_id && (
						<ViewUserLogs oktaId={user.okta_id}>
							<Button variant='ghost' size='icon' className='tw-h-8 tw-w-8'>
								<Logs className='tw-h-4 tw-w-4' />
							</Button>
						</ViewUserLogs>
					)}
				</div>
			);
		},
	},
];
